import { DigitalFormsTypes } from '@frontend/api-digital-forms';
import { isSubsetOfMainArray } from '@frontend/array';
import {
  isWritebackCapableSource,
  getSourceIdsCapableOfMedicalConditionForLocation,
  getSourceIdToLocationIdsAssociationCapableOfMedicalConditionForSelectedLocations,
} from '../writeback-capabilities/writeback-capabilities.utils';
import {
  GetSegregatedDataSourcesFn,
  GetSupportedDataSourceByIdFn,
  GetSourcesWithMedicalConditionsForLocationFn,
  GetSourcesWithMedicalConditionsForSelectedLocationsFn,
  GetCommonSourcesWithMedicalConditionsForSelectedLocationsFn,
  GetSegregatedDataSourcesMapFn,
} from './source-tenants.utils.types';

/**
 * Segregates data sources of a location into supported and unsupported categories.
 * @param dataSources - The data sources to segregate for a location.
 * @param sourceWritebackCapabilities - The writeback capabilities of the sources in a location.
 */
export const getSegregatedDataSources: GetSegregatedDataSourcesFn = ({ dataSources, sourceWritebackCapabilities }) => {
  const supportedSources: DigitalFormsTypes.SchemaAbsent.ModifiedDataSource[] = [];
  const unsupportedSources: DigitalFormsTypes.SchemaAbsent.ModifiedDataSource[] = [];

  for (const source of dataSources) {
    if (!source.id) {
      continue;
    }

    if (isWritebackCapableSource({ sourceId: source.id, sourceWritebackCapabilities })) {
      supportedSources.push({ ...source, isSupported: true });
    } else {
      unsupportedSources.push({ ...source, isSupported: false });
    }
  }

  return {
    supportedSources,
    unsupportedSources,
    supportedSourcesCount: supportedSources.length,
    totalSources: dataSources.length,
  };
};

/**
 * Returns the segregated data sources map for multiple locations.
 * @param dataSourcesMap - The data sources map for the locations.
 * @param wbCapabilitiesMap - The writeback capabilities map for the locations.
 * @param locationIds - The IDs of the locations to process.
 */
export const getSegregatedDataSourcesMap: GetSegregatedDataSourcesMapFn = ({
  dataSourcesMap,
  wbCapabilitiesMap,
  locationIds,
}) => {
  const segregatedDataSourcesMap: DigitalFormsTypes.SchemaAbsent.SegregatedDataSourceResponseMapForLocations = {};
  for (const locationId of locationIds) {
    segregatedDataSourcesMap[locationId] = getSegregatedDataSources({
      dataSources: dataSourcesMap[locationId] || [],
      sourceWritebackCapabilities: wbCapabilitiesMap[locationId] || [],
    });
  }

  return segregatedDataSourcesMap;
};

/**
 * Returns a supported data source by its ID.
 * @param supportedDataSources - The supported data sources in a location to search.
 * @param sourceId - The ID of the source to find.
 * @returns The supported data source with the given ID, or undefined if not found.
 */
export const getSupportedDataSourceById: GetSupportedDataSourceByIdFn = ({ supportedDataSources, sourceId }) => {
  return supportedDataSources.find((source) => source.id === sourceId);
};

/**
 * Gets the sources with medical conditions for a specific location.
 * @param sourceWritebackCapabilities - The writeback capabilities of the sources for the location.
 * @param supportedDataSources - The supported data sources for the location.
 * @returns The sources with medical conditions for the location.
 */
export const getSourcesWithMedicalConditionsForLocation: GetSourcesWithMedicalConditionsForLocationFn = ({
  sourceWritebackCapabilities,
  supportedDataSources,
}) => {
  const sources: DigitalFormsTypes.SchemaAbsent.ModifiedDataSource[] = [];
  const sourceIds = getSourceIdsCapableOfMedicalConditionForLocation({
    sourceWritebackCapabilities,
  });
  for (const sourceId of sourceIds) {
    const source = getSupportedDataSourceById({
      sourceId,
      supportedDataSources,
    });
    if (source) {
      sources.push(source);
    }
  }
  return sources;
};

/**
 * Gets the sources with medical conditions for selected locations.
 * @param locationIds - The IDs of the locations to check.
 * @param writebackCapabilitiesMap - The writeback capabilities map for the locations.
 * @param segregatedDataSourcesMap - The segregated data sources map for the locations.
 * @returns The sources with medical conditions for the selected locations.
 */
export const getSourcesWithMedicalConditionsForSelectedLocations: GetSourcesWithMedicalConditionsForSelectedLocationsFn =
  ({ locationIds, writebackCapabilitiesMap, segregatedDataSourcesMap }) => {
    const sources: DigitalFormsTypes.SchemaAbsent.ModifiedDataSource[] = [];
    const sourceIdToLocationIdsAssociation =
      getSourceIdToLocationIdsAssociationCapableOfMedicalConditionForSelectedLocations({
        writebackCapabilitiesMap,
        locationIds,
      });

    for (const { sourceId, associatedLocationIds } of sourceIdToLocationIdsAssociation) {
      if (associatedLocationIds.length < 1) {
        continue;
      }

      const { supportedSources } = segregatedDataSourcesMap[associatedLocationIds[0]];
      const source = getSupportedDataSourceById({
        sourceId,
        supportedDataSources: supportedSources,
      });
      if (source) {
        sources.push(source);
      }
    }

    return sources;
  };

/**
 * Gets the common sources with medical conditions for selected locations.
 * @param locationIds - The IDs of the locations to check.
 * @param segregatedDataSourcesMap - The segregated data sources map for the locations.
 * @param writebackCapabilitiesMap - The writeback capabilities map for the locations.
 * @returns The common sources with medical conditions for the selected locations.
 */
export const getCommonSourcesWithMedicalConditionsForSelectedLocations: GetCommonSourcesWithMedicalConditionsForSelectedLocationsFn =
  ({ locationIds, segregatedDataSourcesMap, writebackCapabilitiesMap }) => {
    const commonSourceIdsMap = new Map<string, boolean>();
    const commonSources: DigitalFormsTypes.SchemaAbsent.ModifiedDataSource[] = [];
    const sourceIdToLocationIdsAssociation =
      getSourceIdToLocationIdsAssociationCapableOfMedicalConditionForSelectedLocations({
        writebackCapabilitiesMap,
        locationIds,
      });

    for (const { sourceId, associatedLocationIds } of sourceIdToLocationIdsAssociation) {
      if (associatedLocationIds.length < 1) {
        continue;
      }

      // Check if the source is associated with all the asked locations
      const isCommonSource = isSubsetOfMainArray(associatedLocationIds, locationIds);
      if (isCommonSource) {
        commonSourceIdsMap.set(sourceId, true);
        const { supportedSources } = segregatedDataSourcesMap[associatedLocationIds[0]];
        const source = getSupportedDataSourceById({
          sourceId,
          supportedDataSources: supportedSources,
        });
        if (source) {
          commonSources.push(source);
        }
      }
    }

    return {
      commonSources,
      commonSourceIdsMap,
    };
  };
